import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AuthProvider, AuthContext } from "./AuthContext";
import Login from "./Login";
import Register from "./Register";
import CalendarFilter from "./CalendarFilter";
import Dictionary from "./Dictionary";
import "./App.css";

const API_URL = "/api";

function AppContent() {
  const { user, token, login, logout } = useContext(AuthContext);
  const [entries, setEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState("");
  const [showRegister, setShowRegister] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Mantém usuário autenticado ao recarregar
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    if (!user && storedToken && storedUser) {
      try { login(JSON.parse(storedUser), storedToken); } catch {}
    }
  }, []);

  // Configura axios com token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      localStorage.setItem("token", token);
      if (user) localStorage.setItem("user", JSON.stringify(user));
    } else {
      delete axios.defaults.headers.common["Authorization"];
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    }
  }, [token, user]);

  useEffect(() => {
    if (user && token) fetchEntries();
  }, [user, token]);

  async function fetchEntries() {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/entries`);
      setEntries(res.data);
    } catch {
      setError("Failed to load entries.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/entries`, { content });
      setContent("");
      setAnalyzing(true);
      const analyzeRes = await axios.post(`${API_URL}/entries/${res.data.id}/analyze`);
      fetchEntries();
      setSelectedEntry({ ...res.data, analysis: analyzeRes.data.analysis, score: analyzeRes.data.score });
    } catch {
      setError("Failed to create or analyze entry.");
    } finally {
      setLoading(false);
      setAnalyzing(false);
    }
  }

  async function handleAnalyze(entry) {
    setAnalyzing(true);
    setError("");
    try {
      const res = await axios.post(`${API_URL}/entries/${entry.id}/analyze`);
      fetchEntries();
      setSelectedEntry({ ...entry, analysis: res.data.analysis, score: res.data.score });
    } catch {
      setError("Failed to analyze entry.");
    } finally {
      setAnalyzing(false);
    }
  }

  function handleDateChange(date) {
    setSelectedDate(date);
    setSelectedEntry(null);
  }

  const filteredEntries = selectedDate
    ? entries.filter(e => {
        const d = new Date(e.date);
        return (
          d.getFullYear() === selectedDate.getFullYear() &&
          d.getMonth() === selectedDate.getMonth() &&
          d.getDate() === selectedDate.getDate()
        );
      })
    : entries;

  // ── Auth Page ─────────────────────────────────────────
  if (!user) {
    return (
      <div className="auth-page">
        <div className="auth-header">
          <h1>📖 English Diary</h1>
          <p>Improve your English writing with AI-powered feedback</p>
        </div>
        <div className="auth-container">
          {showRegister ? (
            <Register onSwitch={() => setShowRegister(false)} />
          ) : (
            <Login onSwitch={() => setShowRegister(true)} />
          )}
        </div>
      </div>
    );
  }

  // ── App Page ──────────────────────────────────────────
  return (
    <div className="app-container">
      {/* ── Header ── */}
      <header className="app-header">
        <div className="header-content">
          <h1>
            <span className="header-logo-icon">📖</span>
            English Diary with AI
          </h1>
          <div className="user-info">
            <span className="user-greeting">
              Hello, <span>{user.name || user.username}!</span>
            </span>
            <button onClick={logout} className="logout-btn" id="logout-btn">
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* ── Main 3-Column Layout ── */}
      <div className="container">
        <div className="main-content">

          {/* ── Col 1: Calendar ── */}
          <div className="sidebar">
            <div className="panel calendar-panel" id="calendar-panel">
              <div className="panel-header">
                <h3><span className="icon">📅</span> Calendar</h3>
              </div>
              <div className="panel-body">
                <CalendarFilter
                  value={selectedDate}
                  onChange={handleDateChange}
                  entries={entries}
                />
              </div>
            </div>
          </div>

          {/* ── Col 2: Notes ── */}
          <div className="entries-list" id="notes-panel">
            {/* Header */}
            <div className="entries-list-header">
              <h2>
                📝 My Notes
              </h2>
              <span className="entries-count">{filteredEntries.length} note{filteredEntries.length !== 1 ? "s" : ""}</span>
            </div>

            {/* List */}
            <ul>
              {filteredEntries.length === 0 ? (
                <div className="no-entries">No notes for this date.<br/>Write your first entry below! ✍️</div>
              ) : (
                filteredEntries.map(entry => (
                  <li
                    key={entry.id}
                    className={selectedEntry && selectedEntry.id === entry.id ? "selected" : ""}
                    onClick={() => setSelectedEntry(entry)}
                  >
                    <span className="entry-date">
                      {new Date(entry.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                    </span>
                    <span className="entry-preview">{entry.content.slice(0, 60)}…</span>
                  </li>
                ))
              )}
            </ul>

            {/* Error */}
            {error && <div className="error-message">{error}</div>}

            {/* Selected Entry Detail */}
            {selectedEntry && (
              <div className="entry-detail" id="entry-detail">
                <div className="entry-detail-header">
                  <h3>📄 Note Details</h3>
                  {typeof selectedEntry.score === "number" && (
                    <span className="entry-score">
                      🤖 AI Score: {selectedEntry.score}/100
                    </span>
                  )}
                </div>

                <div className="entry-meta">
                  <div className="entry-meta-item">
                    <span className="entry-meta-label">Date</span>
                    <span className="entry-meta-value">
                      {new Date(selectedEntry.date).toLocaleString("en-GB")}
                    </span>
                  </div>
                </div>

                <div className="entry-content-block">
                  <div className="entry-content-text">{selectedEntry.content}</div>
                </div>

                {!selectedEntry.analysis ? (
                  <button
                    className="analyze-btn"
                    onClick={() => handleAnalyze(selectedEntry)}
                    disabled={analyzing}
                    id="analyze-btn"
                  >
                    {analyzing ? (
                      <><span className="spinner" /> Analyzing with AI…</>
                    ) : (
                      <>✨ Analyze with AI</>
                    )}
                  </button>
                ) : (
                  <div className="analysis-container analysis-section">
                    <div className="analysis-badge">AI Feedback ready</div>
                    <div className="analysis-markdown">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          table: ({ node, ...props }) => (
                            <div className="analysis-table-wrapper">
                              <table {...props} />
                            </div>
                          )
                        }}
                      >
                        {selectedEntry.analysis}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* New Entry Form */}
            <div className="entry-form-section">
              <label htmlFor="new-entry-textarea">✍️ New Entry</label>
              <form onSubmit={handleSubmit} className="entry-form">
                <textarea
                  id="new-entry-textarea"
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  placeholder="Write about your day in English… e.g. Today I learned three new words and practiced pronunciation."
                  rows={5}
                  required
                  disabled={loading || analyzing}
                />
                <div className="form-footer">
                  <span className="char-hint">{content.length} characters</span>
                  <button
                    type="submit"
                    disabled={loading || analyzing || !content.trim()}
                    id="save-note-btn"
                  >
                    {loading || analyzing ? (
                      <><span className="spinner" /> {analyzing ? "Analyzing…" : "Saving…"}</>
                    ) : (
                      <>💾 Save & Analyze</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* ── Col 3: Dictionary (sticky) ── */}
          <div className="dictionary-col" id="dictionary-col">
            <Dictionary />
          </div>

        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}